import { useActor } from '@xstate/react';
import { useContext } from 'react';

import {
  DesignStudioServiceContext,
  Mode,
  historyContext,
} from 'src/modules/designStudio/providers';
import type { User } from 'src/shared/graphql/generated';
import { useMode, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';

export const DESIGN_UPDATES_TO_AUTO_OPEN = 5;
const JAMES_HARDIE_HOMEOWNER_ORG = 141718;

const useShouldAutoOpen = ({
  hasClosedDesignMenu,
  hasBeenOpened,
  openPopover,
  triggerButtonBounce,
}: {
  hasClosedDesignMenu: boolean;
  hasBeenOpened: boolean;
  openPopover: (delay?: number) => void;
  triggerButtonBounce: (delay?: number) => void;
}) => {
  const {
    historyState: { designUpdates },
  } = useContext(historyContext);
  if (
    !hasBeenOpened &&
    (designUpdates === DESIGN_UPDATES_TO_AUTO_OPEN || hasClosedDesignMenu)
  ) {
    triggerButtonBounce(250);
    openPopover(850);
  }
};

const useShouldShowRenderingRequest = (): boolean => {
  const { appService } = useContext(DesignStudioServiceContext);
  const [state] = useActor(appService);
  const { jobId, canRequestRendering, hasRenderings } = state.context;
  const mode = useMode();

  return (
    mode === Mode.DEFAULT && !!(canRequestRendering || hasRenderings) && !!jobId
  );
};

const useRenderingRequestCTAContent = (user?: User) => {
  const t = useTranslate(TranslationNamespace.DESIGN_ORDERS);

  if (user?.orgs[0].id === JAMES_HARDIE_HOMEOWNER_ORG) {
    return {
      title: t('cta.homeownerJH.title'),
      header: t('cta.homeownerJH.header'),
      blurb: t('cta.homeownerJH.blurb'),
      buttonText: t('cta.homeownerJH.buttonText'),
    };
  }
  if (user?.isHomeowner) {
    return {
      title: t('cta.homeowner.title'),
      header: t('cta.homeowner.header'),
      blurb: t('cta.homeowner.blurb'),
      buttonText: t('cta.homeowner.buttonText'),
    };
  }
  return {
    title: t('cta.pro.title'),
    header: t('cta.pro.header'),
    blurb: t('cta.pro.blurb'),
    buttonText: t('cta.pro.buttonText'),
  };
};

export {
  useShouldAutoOpen,
  useShouldShowRenderingRequest,
  useRenderingRequestCTAContent,
};
